<template>
    <div :class="$style.toast_container">
        <div :class="[$style.toast, closeButton && $style.toast_close_button]">
            <GhostIconButton
                v-if="closeButton"
                icon="close"
                color="white-60"
                size="m"
                :class="$style.close_override"
                :interfaceTarget="`close-${interfaceTarget}`"
                @click="$emit('onClose')"
            />
            <div :class="$style.instructions">
                <div
                    v-if="animation"
                    :class="$style.animation"
                >
                    <Vue3Lottie :animationData="animation" />
                </div>

                <img
                    v-if="image"
                    :class="$style.image"
                    :src="image"
                    :style="imageStyles"
                    :alt="tr('toast-pwa.description')"
                    loading="lazy"
                />

                <Typography
                    type="header"
                    size="xs"
                    accent
                    :responsive="false"
                    :class="$style.header"
                >
                    {{ title }}
                </Typography>

                <Typography
                    type="paragraph"
                    size="s"
                    :responsive="false"
                    :class="[$style.paragraph, { [$style.no_button_gap]: !buttonText }]"
                >
                    {{ description }}
                </Typography>

                <Button
                    v-if="buttonText"
                    size="m"
                    :class="$style.accept_override"
                    :interfaceTarget="`accept-${interfaceTarget}`"
                    width="full"
                    :label="buttonText"
                    @click="$emit('onAction')"
                />
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { defineAsyncComponent } from 'vue'

import Button from '@/components_new/Buttons/Button.vue'
import GhostIconButton from '@/components_new/Buttons/GhostIconButton.vue'
import Typography from '@/components_new/Typography.vue'
import { useTr } from '@/composables/use-tr'

defineProps<{
    description: string
    title: string
    animation?: Record<string, unknown>
    isFullScreenAllowed?: boolean
    image?: string
    buttonText?: string
    closeButton?: boolean
    altImg?: string
    interfaceTarget: string
    imageStyles?: Record<string, string>
}>()
defineEmits<{ onAction: []; onClose: [] }>()

const tr = useTr()

// load lottie only if animation is present
const Vue3Lottie = defineAsyncComponent(() => import('vue3-lottie').then((mod) => mod.Vue3Lottie))
</script>

<style module>
.toast_container {
    width: 300px;
    min-height: 80px;
    box-sizing: border-box;
}

.toast {
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    max-width: 480px;
    margin: 0 auto;
    padding: 24px 12px 20px;
    background: rgba(23, 23, 26, 0.9);
    border-radius: var(--radius-24);
    backdrop-filter: blur(var(--radius-24));
}

.toast_close_button {
    padding-top: 40px;
}

.close_override {
    position: absolute;
    top: 0;
    right: 0;
}

.instructions {
    display: flex;
    flex-direction: column;
    align-items: center;
}

.image {
    height: 100px;
    margin-bottom: 20px;
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 16px;
}
.animation {
    height: 120px;
    margin-bottom: 20px;
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 16px;
}

.header {
    color: var(--white);
    margin-bottom: 8px;
    text-align: center;
}

.paragraph {
    color: var(--white-60);
    text-align: center;
}

.accept_override {
    margin-top: 20px;
    width: 100%;
}

.no_button_gap {
    margin-bottom: 16px;
}
</style>
